<template>
  <template
    v-for="({ section, items, type }, sectionIndex) in groupedSections"
    :key="`${section?.sys.id ?? items[0].sys.id}-${sectionIndex}`"
  >
    <MappedComponents
      v-if="asSection || type === 'sectionItemEntry'"
      :items="items"
    />
    <LazyContentfulContentSection v-else :section="section" :items="items" />
  </template>
</template>

<script setup lang="ts">
import type { Entry } from '~~/lib/Contentful/types';
import { groupSections } from '~/utils/groupSections';
import { isDefined } from '~/utils/guards/isDefined';
import { isEntryNotAvailableInLanguageError } from '~/utils/guards/isEntryNotAvailableInLanguageError';
import { isEntryOfContentType } from '~/utils/guards/isEntryOfContentType';
import { isLeadPaywall } from '~/utils/paywall/isLeadPaywall';

interface Props {
  // if an entry was deleted but is still referenced, contentful returns
  // `undefined`.
  // @see https://gcp.baslerweb.com/jira/browse/WEB2-2536
  entries: (Entry<unknown> | undefined)[];
  asSection?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  asSection: false
});

const { isValidBot, isPaywallVisible } = usePaywall();

const shouldRenderEntry = (item: Entry<unknown> | undefined): boolean => {
  if (!isDefined(item) || isEntryNotAvailableInLanguageError(item)) {
    return false;
  }

  if (!isEntryOfContentType(item, 'paywall')) {
    return true;
  }

  if (isValidBot.value) {
    return false;
  }

  if (isLeadPaywall(item)) {
    return true;
  }

  return isPaywallVisible.value;
};

const groupedSections = computed(() =>
  groupSections(
    props.entries.filter((item) => shouldRenderEntry(item)) as Entry<unknown>[]
  )
);
</script>
